/**
 * Audio Player State Reducer
 * Implements atomic state updates to prevent race conditions (Bugs #2, #4, #7, #15, #16)
 */

export const initialState = {
  currentEpisode: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  playbackRate: 1,
  pendingSeek: false, // Flag to prevent jitter during manual seeks (Bug #2, #15)
  restoredEpisodeUrl: null // Track restored episode to prevent auto-play (Bug #4)
};

export function audioPlayerReducer(state, action) {
  switch (action.type) {
    // Load episode without playing (Bug #16 fix)
    case 'LOAD_EPISODE':
      return {
        ...state,
        currentEpisode: action.payload.episode,
        currentTime: action.payload.savedTime || 0,
        isPlaying: false,
        pendingSeek: action.payload.savedTime > 0, // Seek to saved position if exists
        restoredEpisodeUrl: null
      };

    // Load and play episode
    case 'PLAY_EPISODE':
      return {
        ...state,
        currentEpisode: action.payload.episode,
        currentTime: action.payload.savedTime || 0,
        isPlaying: true,
        pendingSeek: action.payload.savedTime > 0, // Seek to saved position if exists
        restoredEpisodeUrl: null
      };

    // Restore episode from localStorage without auto-play (Bug #4 fix)
    case 'RESTORE_EPISODE':
      return {
        ...state,
        currentEpisode: action.payload.episode,
        currentTime: action.payload.savedTime || 0,
        volume: action.payload.volume ?? state.volume,
        playbackRate: action.payload.playbackRate ?? state.playbackRate,
        isPlaying: false, // Never auto-play on restore
        restoredEpisodeUrl: action.payload.episode.url
      };

    case 'PLAY':
      return { ...state, isPlaying: true };

    case 'PAUSE':
      return { ...state, isPlaying: false };

    case 'TOGGLE_PLAY':
      return { ...state, isPlaying: !state.isPlaying };

    // Manual seek - set flag to prevent jitter (Bug #2, #15 fix)
    case 'SEEK':
      return {
        ...state,
        currentTime: action.payload.time,
        pendingSeek: true
      };

    // Clear pending seek flag after audio element updates
    case 'CLEAR_PENDING_SEEK':
      return { ...state, pendingSeek: false };

    // Time update from audio element - ignore if pending seek (Bug #2 fix)
    case 'TIME_UPDATE':
      if (state.pendingSeek) return state;
      return { ...state, currentTime: action.payload.time };

    case 'SET_DURATION':
      return { ...state, duration: action.payload.duration };

    case 'SET_VOLUME':
      return { ...state, volume: action.payload.volume };

    case 'SET_PLAYBACK_RATE':
      return { ...state, playbackRate: action.payload.rate };

    // Skip forward/backward
    case 'SKIP':
      const newTime = Math.max(0, Math.min(state.duration, state.currentTime + action.payload.seconds));
      return {
        ...state,
        currentTime: newTime,
        pendingSeek: true
      };

    // Episode ended
    case 'ENDED':
      return {
        ...state,
        isPlaying: false,
        currentTime: 0
      };

    default:
      return state;
  }
}
